import React, { memo, useCallback, useRef, useState } from 'react';
import { View } from 'react-native';

import { BatchFromSearchWithBatchStatistics, ElementSizeConfig, TileComponent, TileProps } from '@v2/context/ProductionBatch/types';
import ColorsWrapper, { checkColor } from '@styles/colors';

import { calculateTimeDifferenceInPercentage, isWeb } from '@helpers';

import { scale } from '@components/frame';

import TouchableElement from '@components/common/touchableElement';

import { getBoxShadowStyle } from '@styles/shadow';
import { useSelector } from 'react-redux';
import { ProductionBatch, ProductionBatchStatistics } from '@interfaces/search';
import { selectBatchForHover, selectBatchList, setBatchTileHeight } from '@reducers/batch';
import { productionBatchStatistics } from '@reducers/statistics';
import { useFocusEffect } from '@react-navigation/native';
import { SCREEN_BORDER_RADIUS, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { getProductiveTimeBlock } from '@components/machines/components/detailsComponents/activeMachine/helpers';
import { useFilterTimeCategoriesData } from '@v2/pages/common/hooks/useFilterTimeCategoriesData';
import { TimeCategoryBlock } from './components/TimeCategoryBlock';
import { ReadyPcsBlock } from './components/ReadyPcsBlock';
import { selectIsWithERP } from '@reducers/membership';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import StatusBadge from '@components/common/StatusBadge';
import BatchImage from './components/BatchImage';
import Separator from '@components/common/Separator';
import InfoBlock from './components/InfoBlock';
import BatchNumberWithStatus from './components/BatchNumberWithStatus';
import { BatchNumberStatusLinkedInfoBlock } from './components/BatchNumberStatusLinkedInfoBlock';
import { GetProductiveTimeBlockParams } from '@components/machines/components/detailsComponents/activeMachine/interfaces';
import { getTimeValues, buildTimeDifferenceConfigs, resolveTileComponent, shouldLoadBatchStatistics } from './helpers';

const WideWebComponent = ({
  batch,
  C,
  elementSizeConfig,
  hovered,
  isWithERP,
  selectedItem,
  batchStatisticsLoaded,
  onPress,
  productiveTimeBlockComponents,
  withoutImage,
}: TileComponent) => {
  return (
    <View style={{ flex: 1, gap: scale(15), flexDirection: 'row', height: scale(120), minHeight: scale(120) }}>
      {!withoutImage && (
        <View style={{ overflow: 'hidden' }}>
          <BatchImage {...{ batch, elementSizeConfig, C, onPress }} />
        </View>
      )}
      <View style={{ justifyContent: 'space-between' }}>
        <BatchNumberStatusLinkedInfoBlock
          batch={batch}
          C={C}
          hovered={hovered}
          isWithERP={isWithERP}
          selectedItem={selectedItem}
          links={batch.links}
          ncFileStatus={batch.ncFileStatus}
          limitWidth
        />

        <InfoBlock batch={batch} C={C} limitWidth />
      </View>

      <View style={{ flex: 1, justifyContent: 'center' }}>
        <TimeCategoryBlock
          batchStatisticsLoaded={batchStatisticsLoaded}
          productiveTimeBlockComponents={productiveTimeBlockComponents}
          batchIsRunning={batch.isInProgress}
        />
      </View>

      <View style={{ justifyContent: 'space-between' }}>
        <View style={{ alignSelf: 'flex-start' }}>
          <StatusBadge C={C} isClosed={batch.isClosed} isInProgress={batch.isInProgress} />
        </View>
        <View style={{ alignSelf: 'flex-end' }}>
          <ReadyPcsBlock C={C} batchStatisticsLoaded={batchStatisticsLoaded} batch={batch} />
        </View>
      </View>
    </View>
  );
};

const WebComponent = ({
  batch,
  C,
  elementSizeConfig,
  hovered,
  isWithERP,
  selectedItem,
  batchStatisticsLoaded,
  onPress,
  productiveTimeBlockComponents,
}: TileComponent) => {
  return (
    <>
      <View style={{ overflow: 'hidden' }}>
        <BatchImage {...{ batch, elementSizeConfig, C, onPress }} />
      </View>
      <View style={{ flex: 1 }}>
        <View
          style={{
            paddingLeft: scale(15),
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <BatchNumberStatusLinkedInfoBlock
            batch={batch}
            C={C}
            hovered={hovered}
            isWithERP={isWithERP}
            selectedItem={selectedItem}
            links={batch.links}
            ncFileStatus={batch.ncFileStatus}
          />
          <StatusBadge C={C} isClosed={batch.isClosed} isInProgress={batch.isInProgress} />
        </View>
        <View
          style={{
            flexDirection: 'row',
            marginTop: scale(12),
            paddingLeft: scale(15),
          }}
        >
          <View style={{ flex: 1, flexDirection: 'row', justifyContent: 'space-between' }}>
            <InfoBlock batch={batch} C={C} />
            <View style={{ justifyContent: 'flex-end' }}>
              <ReadyPcsBlock C={C} batchStatisticsLoaded={batchStatisticsLoaded} batch={batch} />
            </View>
          </View>
        </View>
        <View style={{ paddingLeft: scale(15), marginBlock: scale(13.5) }}>
          <Separator C={C} />
        </View>
        <TimeCategoryBlock
          containerStyles={{ paddingLeft: scale(15) }}
          batchStatisticsLoaded={batchStatisticsLoaded}
          productiveTimeBlockComponents={productiveTimeBlockComponents}
          batchIsRunning={batch.isInProgress}
        />
      </View>
    </>
  );
};

const TabletComponent = ({
  batch,
  C,
  elementSizeConfig,
  productiveTimeBlockComponents,
  withoutImage,
  hovered,
  isWithERP,
  selectedItem,
  batchStatisticsLoaded,
  onPress,
}: TileComponent) => {
  return (
    <>
      <View>
        <View style={{ flexDirection: 'row' }}>
          {!withoutImage && (
            <View style={{ marginRight: scale(15), overflow: 'hidden' }}>
              <BatchImage {...{ batch, C, elementSizeConfig, onPress }} />
            </View>
          )}
          <View style={{ flex: 1 }}>
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <BatchNumberStatusLinkedInfoBlock
                batch={batch}
                C={C}
                hovered={hovered}
                isWithERP={isWithERP}
                selectedItem={selectedItem}
                links={batch.links}
                ncFileStatus={batch.ncFileStatus}
              />
              <StatusBadge C={C} isClosed={batch.isClosed} isInProgress={batch.isInProgress} />
            </View>
            <View
              style={{
                flexDirection: 'row',
                marginTop: scale(12),
              }}
            >
              <View style={{ flex: 1, flexDirection: 'row', justifyContent: 'space-between' }}>
                <InfoBlock batch={batch} C={C} />
                <View style={{ justifyContent: 'flex-end' }}>
                  <ReadyPcsBlock C={C} batchStatisticsLoaded={batchStatisticsLoaded} batch={batch} />
                </View>
              </View>
            </View>
          </View>
        </View>
      </View>
      <View>
        <View style={{ marginBlock: scale(15) }}>
          <Separator C={C} />
        </View>
        <TimeCategoryBlock
          batchStatisticsLoaded={batchStatisticsLoaded}
          productiveTimeBlockComponents={productiveTimeBlockComponents}
          batchIsRunning={batch.isInProgress}
        />
      </View>
    </>
  );
};

const PhoneComponent = ({
  batch,
  C,
  elementSizeConfig,
  productiveTimeBlockComponents,
  withoutImage,
  hovered,
  isWithERP,
  selectedItem,
  batchStatisticsLoaded,
  onPress,
}: TileComponent) => {
  return (
    <View style={{ gap: scale(8) }}>
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          height: scale(25),
        }}
      >
        <BatchNumberWithStatus {...{ batch, C, hovered, isWithERP, selectedItem }} />
        <StatusBadge C={C} isClosed={batch.isClosed} isInProgress={batch.isInProgress} />
      </View>
      {!withoutImage && (
        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
          <BatchImage {...{ batch, C, elementSizeConfig, onPress }} />
          <ReadyPcsBlock C={C} batchStatisticsLoaded={batchStatisticsLoaded} batch={batch} />
        </View>
      )}
      <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
        <InfoBlock batch={batch} C={C} />
        {withoutImage && (
          <View style={{ justifyContent: 'flex-end' }}>
            <ReadyPcsBlock C={C} batchStatisticsLoaded={batchStatisticsLoaded} batch={batch} />
          </View>
        )}
      </View>

      <TimeCategoryBlock
        batchStatisticsLoaded={batchStatisticsLoaded}
        productiveTimeBlockComponents={productiveTimeBlockComponents}
        batchIsRunning={batch.isInProgress}
      />
    </View>
  );
};

const Tile = memo(
  ({
    batch,
    onPress,
    C,
    selectedItem,
    index,
    firstVisibleIndex,
    lastVisibleIndex,
    elementSizeConfig,
    withoutImage = false,
    bg = 'Background/Light/Field',
  }: TileProps) => {
    const ref = useRef<View | null>(null);
    const [hovered, setHovered] = useState<boolean>(false);
    const [batchStatisticsLoaded, setBatchStatisticsLoaded] = useState<boolean>(false);
    const [batchStatistics, setBatchStatistics] = useState<ProductionBatchStatistics | null>(null);

    const dispatch = useAppDispatch();

    const batchForHover = useSelector(selectBatchForHover);
    const batchList = useSelector(selectBatchList);
    const isWithERP = useSelector(selectIsWithERP);

    useFocusEffect(
      useCallback(() => {
        let timeoutId: NodeJS.Timeout;
        const abortController = new AbortController();

        const fetchBatchStatistics = async () => {
          const result = await dispatch(
            productionBatchStatistics(
              {
                productionBatchId: batch.productionBatchId,
              },
              abortController.signal
            )
          );
          setBatchStatistics({ ...result?.statistics, links: batch.links, ncFileStatus: batch.ncFileStatus });
          setBatchStatisticsLoaded(true);
        };

        if (index >= firstVisibleIndex && index <= lastVisibleIndex && shouldLoadBatchStatistics(batch)) {
          const wasLoaded = batchList.find((b: ProductionBatch) => b.productionBatchId === batchStatistics?.productionBatchId);
          if (!wasLoaded) {
            timeoutId = setTimeout(() => {
              fetchBatchStatistics();
            }, 500);
          }
        }

        return () => {
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          abortController.abort();
        };
      }, [
        firstVisibleIndex,
        lastVisibleIndex,
        index,
        batch.productionBatchId,
        batch.links?.length,
        batch.isInProgress,
        batchList,
        batchStatistics?.productionBatchId,
      ])
    );

    const timeValues = getTimeValues(batch, batchStatistics);

    const batchFromSearchWithStatistics: BatchFromSearchWithBatchStatistics = {
      ...((batchStatistics ?? {}) as ProductionBatchStatistics),
      ...batch,
    };

    const timeDifferenceConfigs: GetProductiveTimeBlockParams['timeDifferenceConfigs'] = buildTimeDifferenceConfigs(
      timeValues,
      calculateTimeDifferenceInPercentage
    );

    const productiveTimeBlockComponents = getProductiveTimeBlock({
      batch: batchFromSearchWithStatistics,
      C,
      timeDifferenceConfigs,
      styles: {
        mainContainerStyles: { paddingRight: 0 },
        leftBlockStyles: { flexGrow: 0, flexShrink: 0, flexBasis: scale(130) },
        rightBlockStyles: { gap: scale(10), flex: 1 },
      },
    });

    const filteredProductiveTimeBlockComponents = useFilterTimeCategoriesData({
      data: productiveTimeBlockComponents,
      batch: batchFromSearchWithStatistics,
    });

    const props: TileComponent = {
      batch: batchFromSearchWithStatistics,
      C,
      elementSizeConfig,
      hovered,
      isWithERP,
      selectedItem,
      batchStatisticsLoaded,
      onPress,
      productiveTimeBlockComponents: filteredProductiveTimeBlockComponents,
      withoutImage,
    };

    const componentMap = {
      small: <PhoneComponent {...props} />,
      middle: <TabletComponent {...props} />,
      large: withoutImage ? <WideWebComponent {...props} /> : <WebComponent {...props} />,
      ultraLarge: <WideWebComponent {...props} />,
    } as const;

    const tileComponent = resolveTileComponent(elementSizeConfig) as keyof ElementSizeConfig['elementSizeConfig'];

    const content = (
      <View
        style={[
          getBoxShadowStyle({ type: 'Narrow', C }),
          {
            width: '100%',
            backgroundColor: C(bg),
            borderRadius: scale(SCREEN_BORDER_RADIUS),
          },
        ]}
        ref={ref}
        onLayout={(event) => {
          const { height } = event.nativeEvent.layout;
          dispatch(setBatchTileHeight(height));
        }}
      >
        <TouchableElement
          testID={TEST_IDS.searchScreen.productionBatches.infoTileTile}
          onPress={() => {
            if (onPress) {
              onPress(batch);
            }
          }}
        >
          {({ pressHoverColor, boxStyle }) => (
            <View>
              <View
                style={[
                  boxStyle,
                  {
                    width: '100%',
                    paddingHorizontal: scale(elementSizeConfig.small ? 12 : 15),
                    paddingVertical: scale(elementSizeConfig.small ? 12 : 15),
                    borderRadius: scale(SCREEN_BORDER_RADIUS),
                    backgroundColor:
                      batchForHover.id === batch.productionBatchId && batchForHover.shouldHover
                        ? C(checkColor(bg, 'pressed'))
                        : C(pressHoverColor(bg)),
                    flexDirection: !elementSizeConfig.large && !elementSizeConfig.ultraLarge ? 'column' : 'row',
                  },
                ]}
              >
                {componentMap[tileComponent]}
              </View>
            </View>
          )}
        </TouchableElement>
      </View>
    );

    if (isWeb) {
      return (
        <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
          {content}
        </div>
      );
    }

    return content;
  }
);

export default ColorsWrapper(Tile);
